import { useId, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export type SwitchProps = {
  label?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  style?: CSSProperties;
  'aria-label'?: string;
};

/** Switch — on/off toggle. The knob settles with the soft ease, no overshoot. */
export function Switch({
  label,
  checked,
  defaultChecked,
  onChange,
  disabled = false,
  size = 'md',
  id,
  style,
  ...rest
}: SwitchProps) {
  const isControlled = checked !== undefined;
  const [inner, setInner] = useState(!!defaultChecked);
  const on = isControlled ? checked : inner;
  const autoId = useId();
  const swId = id || autoId;

  const W = size === 'sm' ? 40 : 48;
  const H = size === 'sm' ? 24 : 28;
  const knob = H - 6;

  const toggle = () => {
    if (disabled) return;
    if (!isControlled) setInner((v) => !v);
    onChange?.(!on);
  };

  return (
    <label
      htmlFor={swId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        ...style,
      }}
    >
      <input
        id={swId}
        type="checkbox"
        role="switch"
        checked={on}
        onChange={toggle}
        disabled={disabled}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
        {...rest}
      />
      <span
        style={{
          position: 'relative',
          width: W,
          height: H,
          flexShrink: 0,
          borderRadius: 'var(--radius-pill)',
          background: on ? 'var(--brand)' : 'var(--sand-300)',
          transition: 'background var(--duration-normal) var(--ease-soft)',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 3,
            left: 3,
            width: knob,
            height: knob,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: 'var(--shadow-sm)',
            transform: on ? `translateX(${W - knob - 6}px)` : 'translateX(0)',
            transition: 'transform var(--duration-normal) var(--ease-soft)',
          }}
        />
      </span>
      {label ? (
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-md)', color: 'var(--text)' }}>
          {label}
        </span>
      ) : null}
    </label>
  );
}
